import { Form, Input, InputNumber } from 'antd';
import classNames from 'classnames';
import { isNumber, isString } from 'lodash-es';
import { useCallback, useRef } from 'react';

import { numberOr } from '../../common/utils/numberUtils';
import { FormItem } from './FormItem';
import { isFormValuePresent } from './formHelpers';
import { useBasicFormElementsStyles } from './basicFormElements.styles';

function InputText({ className, ...rest }) {
  const { styles } = useBasicFormElementsStyles();

  return (
    <Input
      className={classNames(
        styles.inputText,
        'InputText',
        { 'has-prefix': !!rest.prefix },
        className
      )}
      autoComplete="off"
      {...rest}
    />
  );
}
export function FormItemInputText({
  onFocus,
  onBlur,
  onKeyDown,
  formItemComponentProps,
  ...rest
}) {
  return (
    <FormItem
      {...rest}
      formItemComponentProps={{
        onFocus,
        onBlur,
        onKeyDown,
        ...formItemComponentProps,
      }}
      component={InputText}
    />
  );
}

export function FormItemInputPassword({
  formItemComponentProps = {},
  ...rest
}) {
  const { styles } = useBasicFormElementsStyles();
  const { className, ...restFormItemComponentProps } = formItemComponentProps;

  return (
    <FormItem
      {...rest}
      formItemComponentProps={{
        className: classNames(styles.passwordWrapper, className),
        ...restFormItemComponentProps,
      }}
      component={Input.Password}
    />
  );
}

export function FormItemInputTextArea({
  formItemComponentProps = {},
  ...rest
}) {
  const { styles } = useBasicFormElementsStyles();
  const { className, ...restFormItemComponentProps } = formItemComponentProps;

  return (
    <FormItem
      {...rest}
      formItemComponentProps={{
        className: classNames(styles.inputTextArea, className),
        ...restFormItemComponentProps,
      }}
      floatingLabel={false}
      component={Input.TextArea}
    />
  );
}

const InputHidden = props => <Input {...props} style={{ display: 'none' }} />;
export function FormItemInputHidden(props) {
  return (
    <Form.Item {...props}>
      <InputHidden />
    </Form.Item>
  );
}
/**
 * This serves as a registration for ant form. Since the real form item is in dialog and thus
 * not rendered at the start, it would cause form to ignore the value when setting it programatically
 */
export const FormItemFieldRegistration = props => (
  <FormItemInputHidden {...props} noStyle />
);

function InputNumberAdapter({ className, suffixIcon, ...rest }) {
  const { styles } = useBasicFormElementsStyles();

  // InputNumber doesn't support `suffix` prop, so we have to apply this hack
  return (
    <>
      <InputNumber
        className={classNames(styles.inputNumber, className)}
        {...rest}
      />
      <div className={styles.formItemSuffix}>{suffixIcon}</div>
    </>
  );
}

function parseNumber(val) {
  if (isNumber(val)) {
    return val;
  }
  if (!val) {
    return undefined;
  }
  if (isString(val)) {
    return numberOr(parseFloat(val), undefined);
  }
  return null;
}

function normalizeInteger(value) {
  if (value == null || value === '') {
    return value;
  }

  const num = Number(value);

  return Number.isNaN(num) ? value : Math.round(num);
}

export function FormItemInputNumber({
  suffix,
  min,
  max,
  step,
  formItemComponentProps,
  ...rest
}) {
  // Numeric part of the text
  const lastText = useRef('');

  // value -> text
  const suffixFormatter = useCallback(
    val => {
      const formattedValue = isFormValuePresent(val)
        ? `${numberOr(parseFloat(val), '')}`
        : val;

      const needsInitialization = !isFormValuePresent(lastText.current);
      const wasUpdated =
        isFormValuePresent(lastText.current) &&
        parseFloat(lastText.current) !== val;

      if (needsInitialization || wasUpdated) {
        lastText.current = formattedValue;
      }

      // We are returning the text as it is to prevent losing '.' during typing
      return isFormValuePresent(lastText.current)
        ? `${lastText.current}${suffix}`
        : '';
    },
    [suffix]
  );
  // text -> value
  const suffixParser = useCallback(
    val => {
      lastText.current = val.replace(suffix, '').replace(/[^0-9.+-]/g, '');
      return isFormValuePresent(val)
        ? numberOr(parseFloat(lastText.current))
        : undefined;
    },
    [suffix]
  );

  const precision = formItemComponentProps?.precision ?? 0;
  const normalize = precision === 0 ? normalizeInteger : undefined;

  return (
    <FormItem
      {...rest}
      component={InputNumberAdapter}
      parseValue={parseNumber}
      normalize={normalize}
      formItemComponentProps={{
        parser: suffix ? suffixParser : undefined,
        formatter: suffix ? suffixFormatter : undefined,
        min,
        max,
        step,
        precision,
        ...formItemComponentProps,
      }}
    />
  );
}
